import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import log from 'loglevel';
import { Divider } from '../../../Common/Divider';
import STRING_KEYS from '../../../../language/keys';
import TimeToPayDialog from '../time-to-pay-dialog/time-to-pay-dialog.component';
import { usePaymentInteraction } from '../../../../hooks/payment-react-qury.hooks';
import { AppRouting } from '../../../../services/appRoutingResolver';
import { TimeToPayDialogActionVariant } from '../time-to-pay-dialog/time-to-pay-dialog.enums';
import { useGetMyAccountSelector } from '../../../../store/rtk-query/apis/account/selectors/get-account-by-id.selector';
import { TimeToPayDialogButton } from '../time-to-pay-dialog/time-to-pay-dialog';
import { useStartTrialRequestMutation } from '../../../../store/rtk-query/apis/account-plan/hooks/start-trial.mutation-hook';
import { StartTrialSuccessDialog } from '../../start-trial-success-dialog/start-trial-success-dialog.component';
import { accountQuery } from '../../../../store/rtk-query/apis/account/account.toolkit-api';
import { AlertSnackBar } from '../../../Common/CustomSnackBar/AlertSnackBar';
import { useAnalyticsContext } from '../../../../contexts/analytics/useAnalyticsContext';
import {
  ANALYTICS_EVENTS,
  EVENT_GROUP,
} from '../../../../consts/analytics';
import {
  CheckedIcon, Suggestion, SuggestionsContainer, SuggestionsItem,
} from './upgrade-to-pro-dialog.styled';
import { UpgradeToProDialogType } from './upgrade-to-pro-dialog.enums';
import { UpgradeToProDialogProps } from './upgrade-to-pro-dialog';

const suggestions = [
  STRING_KEYS.UPGRADE_TO_PRO_DIALOG.SUGGESTIONS.UNLIMITED_DAILY_MATCHES,
  STRING_KEYS.UPGRADE_TO_PRO_DIALOG.SUGGESTIONS.UNLIMITED_POSITIONS,
  STRING_KEYS.UPGRADE_TO_PRO_DIALOG.SUGGESTIONS.ACCESS_ALL_FEATURES,
];

const logger = log.getLogger('UPGRADE_TO_PRO_DIALOG');
const MAX_FETCH_ACCOUNT_RETRIES = 2;

const UpgradeToProDialog = ({
  isOpen,
  onClose,
  onActionClick,
  title,
  subTitle,
  referrer,
  eventMetadata,
}: UpgradeToProDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const { mutate: createCheckoutSession, isLoading: creatingCheckoutSession } = usePaymentInteraction();
  const { data: { wasInTrial, id: accountId } } = useGetMyAccountSelector();
  const [dialogType, setDialogType] = useState<UpgradeToProDialogType | null>(UpgradeToProDialogType.UpgradeDialog);
  const [isAlertSnackbarOpen, setIsAlertSnackbarOpen] = useState<boolean>(false);
  const { analyticsTrackEvent } = useAnalyticsContext();
  const [startTrial, {
    isSuccess: isStartTrialSuccess,
    isLoading: isStartTrialLoading,
    isError: isStartTrialError,
    error: startTrialError,
  }] = useStartTrialRequestMutation();
  const [getAccount, { isLoading: isGetAccountLoading }] = accountQuery.useLazyGetAccountByIdQuery();

  const fetchAccount = async (retryCount = 1) => {
    if (retryCount > MAX_FETCH_ACCOUNT_RETRIES) {
      window.location.reload();

      return;
    }

    const {
      data, isError, error,
    } = await getAccount(accountId);
    if (data) {
      setDialogType(UpgradeToProDialogType.TrialStartSuccessDialog);
    } else if (isError) {
      logger.error('Error in get account', error, retryCount);
      fetchAccount(retryCount + 1);
    }
  };

  useEffect(() => {
    if (isStartTrialSuccess) {
      fetchAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartTrialSuccess]);

  useEffect(() => {
    if (isStartTrialError) {
      logger.error('Error in start account trial', accountId, startTrialError);
      setIsAlertSnackbarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartTrialError]);

  const renderSuggestions = () => suggestions.map((key) => (
    <SuggestionsItem key={ key } >
      <CheckedIcon />
      <Suggestion>
        { translate(key) }
      </Suggestion>
    </SuggestionsItem>
  ));

  const sendClickedOnGoProBiEvent = () => {
    if (!eventMetadata) {
      return;
    }

    analyticsTrackEvent({
      eventName: ANALYTICS_EVENTS.CLICKED_ON_GO_PRO,
      eventGroup: EVENT_GROUP.CLICK,
      productArea: eventMetadata.productArea,
      pageName: eventMetadata.pageName,
      referral: eventMetadata.referral,
    });
  };

  const onClickAction = () => {
    onActionClick?.();
    sendClickedOnGoProBiEvent();
    const successUrl = new URL(window.location.href);
    if (referrer) {
      successUrl.searchParams.append(AppRouting.URL_PARAMS.REFERRER, referrer);
    }
    createCheckoutSession(successUrl.toString());
  };

  const onTryForFreeButtonClick = () => {
    startTrial();
    analyticsTrackEvent({
      eventName: ANALYTICS_EVENTS.CLICKED_ON_TRY_FOR_FREE_IN_UPGRADE_TO_PRO_DIALOG,
      eventGroup: EVENT_GROUP.CLICK,
    });
  };

  const dialogButtons = useMemo(() => {
    const buttons: TimeToPayDialogButton[] = [
      {
        title: translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.UPGRADE_BUTTON_TITLE),
        clickHandler: onClickAction,
        isLoading: creatingCheckoutSession,
      },
    ];

    if (!wasInTrial) {
      buttons.unshift({
        title: translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.TRY_FOR_FREE_BUTTON_TITLE),
        clickHandler: onTryForFreeButtonClick,
        isLoading: isStartTrialLoading || isGetAccountLoading,
        variant: TimeToPayDialogActionVariant.Text,
      });
    }

    return buttons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatingCheckoutSession, isGetAccountLoading, isStartTrialLoading, translate, wasInTrial]);

  const renderTimeToPayDialog = () => {
    return (
      <TimeToPayDialog
        isOpen
        onClose={ onClose }
        title={ title }
        subTitle={ subTitle }
        buttons={ dialogButtons }
        footerTitle={ translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.FOOTER_TITLE) }
        footerSubTitle={ translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.FOOTER_SUB_TITLE) }
      >
        <SuggestionsContainer>
          { renderSuggestions() }
        </SuggestionsContainer>
        <Divider />
      </TimeToPayDialog>
    );
  };

  const renderSuccessDialog = () => {
    return (
      <StartTrialSuccessDialog
        isDialogOpen
        closeHandler={ onClose }
        actionHandler={ onClose }
      />
    );
  };

  const renderDialog = (): JSX.Element | undefined => {
    if (!isOpen || isAlertSnackbarOpen) {
      return undefined;
    }

    if (dialogType === UpgradeToProDialogType.UpgradeDialog) {
      return renderTimeToPayDialog();
    }

    if (dialogType === UpgradeToProDialogType.TrialStartSuccessDialog) {
      return renderSuccessDialog();
    }

    return undefined;
  };

  const onAlertSnackbarClose = () => {
    setIsAlertSnackbarOpen(false);
    onClose();
  };

  return (
    <>
      { renderDialog() }
      { isAlertSnackbarOpen && (
        <AlertSnackBar
          title={ translate(STRING_KEYS.ALERT) }
          description={ translate(STRING_KEYS.GLOBAL_ERROR_MESSAGE) }
          isOpen={ isAlertSnackbarOpen }
          onClose={ onAlertSnackbarClose }
          shouldAutoDismiss
        />
      ) }
    </>
  );
};

export default UpgradeToProDialog;
